// src/components/layout/SectionFeedbackPrompt.jsx
import { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/client";

const DIFFICULTIES = [
  { value: "too_easy", label: "Too easy" },
  { value: "just_right", label: "Just right" },
  { value: "too_hard", label: "Too hard" },
];

// Track skips per session to stop prompting after 2 consecutive skips
const sessionSkips = { count: 0 };

export default function SectionFeedbackPrompt({
  workbookId,
  sectionId,
  sectionTitle,
  onDone,
}) {
  const [clarity, setClarity] = useState(0); // 1–5, 0 = not set
  const [hovered, setHovered] = useState(0);
  const [difficulty, setDifficulty] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Set when the submit came back 401. Keeps the prompt open.
  const [authError, setAuthError] = useState(false);

  // Trap focus inside modal
  useEffect(() => {
    const prev = document.activeElement;
    return () => prev?.focus();
  }, []);

  // 1. handleSkip first
  const handleSkip = useCallback(() => {
    sessionSkips.count += 1;
    onDone(false);
  }, [onDone]);

  // 2. then the useEffect that uses it
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleSkip();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSkip]);

  const handleSubmit = async () => {
    if (!clarity || !difficulty) return;
    setSubmitting(true);
    setAuthError(false);
    try {
      await apiClient.post("/api/feedback/section", {
        workbookId,
        sectionId,
        clarityRating: clarity,
        difficulty,
        feedbackText: text.trim() || null,
      });
      sessionSkips.count = 0; // reset skip streak on submit
      setSubmitted(true);
      setTimeout(() => onDone(true), 1200);
    } catch (err) {
      // A 401 is recoverable: hold the prompt open so the ratings and the
      // typed note survive.
      if (err?.status === 401) setAuthError(true);
      else onDone(); // silent fail — never block user
    } finally {
      setSubmitting(false);
    }
  };

  const starLabel = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleSkip}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,15,20,0.45)",
          backdropFilter: "blur(3px)",
          zIndex: 1000,
          animation: "fbFadeIn 180ms ease",
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Section feedback"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1001,
          width: "min(92vw, 440px)",
          background: "#F5F2EC",
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
          padding: "2rem",
          animation: "fbSlideUp 220ms cubic-bezier(.22,.68,0,1.2)",
          fontFamily: "inherit",
        }}
      >
        {submitted ? (
          /* ── Thank you state ── */
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <div style={{ fontSize: 36, marginBottom: ".5rem" }}>🎉</div>
            <p
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 18,
                fontWeight: 700,
                color: "#1a1a1a",
                margin: 0,
              }}
            >
              Thanks for the feedback!
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#2563eb",
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    margin: "0 0 4px",
                  }}
                >
                  Quick feedback
                </p>
                <h3
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#1a1a1a",
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  How was this section?
                </h3>
                {sectionTitle && (
                  <p
                    style={{
                      fontSize: 12,
                      color: "#71717a",
                      margin: "4px 0 0",
                      fontStyle: "italic",
                    }}
                  >
                    "{sectionTitle}"
                  </p>
                )}
                {/* add this line */}
                <p
                  style={{ fontSize: 18, color: "#a1a1aa", margin: "6px 0 0" }}
                >
                  Help us deliver you the best workbook 🎯
                </p>
              </div>
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#a1a1aa",
                  fontSize: 20,
                  padding: "0 0 0 8px",
                  lineHeight: 1,
                }}
                aria-label="Skip feedback"
              >
                ×
              </button>
            </div>

            {/* Clarity stars */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                Clarity
              </label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setClarity(n)}
                    onMouseEnter={() => setHovered(n)}
                    onMouseLeave={() => setHovered(0)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 28,
                      padding: "2px 1px",
                      lineHeight: 1,
                      color: n <= (hovered || clarity) ? "#f59e0b" : "#d4d4d8",
                      transition: "color 100ms, transform 100ms",
                      transform:
                        n <= (hovered || clarity) ? "scale(1.15)" : "scale(1)",
                    }}
                  >
                    ★
                  </button>
                ))}
                {(hovered || clarity) > 0 && (
                  <span
                    style={{ fontSize: 12, color: "#71717a", marginLeft: 4 }}
                  >
                    {starLabel[hovered || clarity]}
                  </span>
                )}
              </div>
            </div>

            {/* Difficulty pills */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                Difficulty
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {DIFFICULTIES.map((d) => {
                  const active = difficulty === d.value;
                  return (
                    <button
                      key={d.value}
                      onClick={() => setDifficulty(d.value)}
                      style={{
                        border: active
                          ? "1.5px solid #2563eb"
                          : "1.5px solid #e4e4e7",
                        background: active ? "#eff6ff" : "white",
                        color: active ? "#2563eb" : "#52525b",
                        borderRadius: 8,
                        padding: "6px 14px",
                        fontSize: 13,
                        fontWeight: active ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 120ms",
                        fontFamily: "inherit",
                      }}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional text */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                Anything unclear?{" "}
                <span style={{ fontWeight: 400, color: "#a1a1aa" }}>
                  (optional)
                </span>
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. the interface example was hard to follow..."
                maxLength={1000}
                rows={2}
                style={{
                  width: "100%",
                  border: "1.5px solid #e4e4e7",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  color: "#1a1a1a",
                  background: "white",
                  resize: "vertical",
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                  lineHeight: 1.55,
                }}
              />
            </div>

            {/* A 401 keeps the prompt open with everything the user typed
                still in it. Closing on failure, which is what the silent
                onDone() below does for every other error, would throw the
                text away. */}
            {authError && (
              <div
                role="alert"
                style={{
                  background: "#FEF3C7",
                  border: "1px solid #FDE68A",
                  color: "#78350F",
                  borderRadius: 8,
                  padding: "9px 12px",
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                Your session expired. Sign in again, then resend this.
              </div>
            )}

            {/* Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  color: "#71717a",
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "8px 0",
                  fontFamily: "inherit",
                }}
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                disabled={!clarity || !difficulty || submitting}
                style={{
                  background: !clarity || !difficulty ? "#e4e4e7" : "#2563eb",
                  color: !clarity || !difficulty ? "#a1a1aa" : "white",
                  border: "none",
                  borderRadius: 9,
                  padding: "9px 22px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: !clarity || !difficulty ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "background 150ms",
                }}
              >
                {submitting ? "Saving..." : "Submit feedback"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fbFadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fbSlideUp { from { opacity: 0; transform: translate(-50%, calc(-50% + 16px)) } to { opacity: 1; transform: translate(-50%, -50%) } }
      `}</style>
    </>
  );
}

// Utility: should we show the prompt this time?
export function shouldShowFeedback(sectionId, completedFeedbackIds) {
  if (sessionSkips.count >= 2) return false; // skipped 2 in a row this session
  if (completedFeedbackIds.has(sectionId)) return false; // already rated
  return true;
}
